/**
 * **Ver la baldosa.** Las fotos de una línea del pedido y de su referencia (0063, 29-sep).
 *
 * Hoy un pedido se lee «Baldosa Lisa 20×20 · Neblina E1» y ni quien vende ni quien programa ve
 * **qué pieza es**. El texto identifica; la foto reconoce. Por eso todo lo de aquí es un añadido
 * al lado del nombre y nunca su sustituto.
 *
 * Vive aparte de `api.ts` a propósito: son rutas nuevas de un módulo nuevo (`app/modules/images`)
 * y no hay ninguna razón para mezclarlas con las del pedido.
 *
 * **Se sube como data URL en JSON** —lo que da el portapapeles y lo que da `FileReader`— porque el
 * backend lo recibe así y porque D-032 no deja meter una librería de subidas nueva.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api";

/**
 * Los mismos límites que defiende el servidor (`app/modules/images/router.py`) y, debajo, la base.
 *
 * Están copiados aquí **para decirlos antes de chocar**, no para decidir en su lugar: si el servidor
 * rechaza igual, su mensaje se enseña tal cual. Una pantalla que se inventa su propio motivo manda a
 * corregir el problema equivocado.
 */
export const LIMITES = {
  tipos: ["image/png", "image/jpeg", "image/webp"] as const,
  /** 2 MB, contados sobre el archivo: es lo que el servidor mide tras descodificar el base64. */
  bytes: 2 * 1024 * 1024,
  /** El tope por línea lo pone la base; pasarse contesta 409 con su motivo en castellano. */
  porLinea: 5,
} as const;

/** «PNG, JPEG o WebP, hasta 2 MB, cinco por línea» — dicho una sola vez, en un solo sitio. */
export const TEXTO_LIMITES = `PNG, JPEG o WebP · hasta 2 MB · ${LIMITES.porLinea} por línea`;

/**
 * Una foto de una línea del pedido, como la lista `GET /api/order-lines/{id}/images`.
 *
 * **El contenido no viene aquí**: viene `url`, que es la dirección de la imagen y se usa tal cual
 * como `src` — va con la sesión del navegador, no hay nada que firmar.
 *
 * Se escribe a mano porque el servidor devuelve estas filas como `dict[str, Any]` y el contrato no
 * las describe: **el tipo de aquí no es más verdad que la respuesta**, así que todo lo que se lee de
 * ella se comprueba antes (`fotosDe`).
 */
export type FotoDeLinea = {
  id: string;
  url: string;
  note?: string | null;
  media_type?: string | null;
  byte_size?: number | null;
};

/**
 * **La dirección de la foto que trae una fila, si es que la trae.**
 *
 * `image_url` es opcional en el contrato (`TimelineLine`, `QueueOrder`) y en la cola de producción ni
 * siquiera está declarado en el tipo escrito a mano. Leerlo con `as` daría por bueno cualquier valor:
 * un `""` o un número pintarían una imagen rota, que es peor que no pintar nada. Se comprueba la
 * forma y se devuelve `null` en todo lo demás.
 */
export function urlDeFoto(fila: unknown): string | null {
  if (!fila || typeof fila !== "object") return null;
  const url = (fila as { image_url?: unknown }).image_url;
  return typeof url === "string" && url.length > 0 ? url : null;
}

/** Las fotos de una respuesta, quedándose solo con las filas que de verdad tienen `id` y `url`. */
export function fotosDe(datos: unknown): FotoDeLinea[] {
  if (!Array.isArray(datos)) return [];
  return datos.filter(
    (f): f is FotoDeLinea =>
      !!f &&
      typeof f === "object" &&
      typeof (f as FotoDeLinea).id === "string" &&
      typeof (f as FotoDeLinea).url === "string",
  );
}

/**
 * **Lo que está mal en este archivo, dicho antes de mandarlo.**
 *
 * Devuelve `null` si se puede subir. No sustituye al servidor —él vuelve a medir, y manda él— pero
 * evita el viaje y, sobre todo, evita que quien sube un HEIC de 6 MB descubra el motivo después de
 * treinta segundos de espera.
 */
export function revisarArchivo(file: File): string | null {
  if (!(LIMITES.tipos as readonly string[]).includes(file.type)) {
    return `«${file.type || "ese archivo"}» no se puede subir: se aceptan PNG, JPEG y WebP.`;
  }
  if (file.size > LIMITES.bytes) {
    return `La imagen pesa ${(file.size / 1024 / 1024).toFixed(1)} MB: el tope es 2 MB. Achícala o recórtala antes de subirla.`;
  }
  return null;
}

/**
 * Lee un archivo como data URL.
 *
 * `FileReader` y no `fetch` del blob: es la única forma de sacar un data URL de lo que da el
 * portapapeles sin una librería nueva. El `onerror` se ata de verdad — sin él, un archivo ilegible
 * dejaba la promesa colgada y el botón en «Subiendo…» para siempre.
 */
export function leerComoDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r === "string" && r.startsWith("data:")) resolve(r);
      else reject(new Error("La imagen no se pudo leer: vuelve a pegarla o elige otro archivo."));
    };
    reader.onerror = () => reject(new Error("La imagen no se pudo leer: vuelve a pegarla o elige otro archivo."));
    reader.readAsDataURL(file);
  });
}

/**
 * La primera imagen de un evento de pegado, si la hay.
 *
 * Al pegar una captura, `clipboardData.items` trae a la vez la imagen y una versión en texto o HTML;
 * quedarse con la primera entrada sin mirar su tipo pega el HTML y manda un data URL que el servidor
 * rechaza con un 422 que no explica nada.
 */
export function imagenPegada(data: DataTransfer | null): File | null {
  if (!data) return null;
  const items = Array.from(data.items ?? []);
  for (const item of items) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) return file;
    }
  }
  const files = Array.from(data.files ?? []);
  return files.find((f) => f.type.startsWith("image/")) ?? null;
}

/* ── Las fotos de una línea del pedido ────────────────────────────────────────── */

export function useFotosDeLinea(orderLineId: string | undefined, habilitado = true) {
  return useQuery({
    queryKey: ["order-line-images", orderLineId],
    queryFn: () => apiGet<unknown>(`/order-lines/${orderLineId}/images`),
    enabled: !!orderLineId && habilitado,
    select: fotosDe,
  });
}

/**
 * Sube una foto a la línea.
 *
 * **Refresca también la ficha del pedido**, no solo la lista: `image_url` de la línea sale de la
 * primera foto, así que sin invalidar `order-timeline` la miniatura de la tabla seguiría vacía
 * después de subirla — y quien la sube concluye que no se guardó y la sube otra vez.
 */
export function useSubirFotoDeLinea(orderLineId: string | undefined, orderId?: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { data_url: string; note?: string | null }) =>
      apiPost<unknown>(`/order-lines/${orderLineId}/images`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["order-line-images", orderLineId] });
      void client.invalidateQueries({ queryKey: ["order-timeline", orderId] });
      void client.invalidateQueries({ queryKey: ["production", "queue"] });
    },
  });
}

export function useQuitarFotoDeLinea(orderLineId: string | undefined, orderId?: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) => apiDelete(`/order-line-images/${imageId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["order-line-images", orderLineId] });
      void client.invalidateQueries({ queryKey: ["order-timeline", orderId] });
      void client.invalidateQueries({ queryKey: ["production", "queue"] });
    },
  });
}

/* ── La foto de la referencia ─────────────────────────────────────────────────── */

/**
 * Pone la foto de la **referencia**: la que sale en cualquier línea que no tenga la suya.
 *
 * Es otro permiso —`catalogo.editar`, no `pedidos.editar`— porque es otra cosa: cambiarla toca todos
 * los pedidos del catálogo, no este. Queda aquí para que las dos rutas se lean juntas; la usa la
 * Bodega de producto al crear una referencia de reventa con foto (3-nov), que conoce la referencia
 * solo después de crearla: por eso `reference_id` puede venir en la llamada.
 */
export function usePonerFotoDeReferencia(referenceId?: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ reference_id = referenceId, ...body }: { data_url: string; note?: string | null; reference_id?: string }) =>
      apiPut<unknown>(`/product-references/${reference_id}/image`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["order-timeline"] });
      void client.invalidateQueries({ queryKey: ["product-references"] });
    },
  });
}
